/**
 * Pure data derivation for the Scene Breakdown view (no React / UI deps, unit-tested).
 *
 * Produces one normalized row per scene from a loaded director-script envelope, preferring the
 * orchestrator's structured `diagnostics.scene_breakdown` and falling back to deriving the same
 * shape from the raw script scenes (splitting the camera direction back out of the prompt).
 */

// Must stay in sync with configs.skills._CAMERA_PROMPT_MARKER in the orchestrator.
export const CAMERA_PROMPT_MARKER = " Camera move: ";

export type SceneBreakdownEntry = {
  sceneId: string | number | null;
  index: number;
  type: string | null;
  animated: boolean;
  promoted: boolean;
  cameraMove: string | null;
  cameraDirection: string | null;
  basePrompt: string | null;
  file: string | null;
  length: number | null;
  transition: string | null;
  titleText: string | null;
  // Scout-pinned parallax depth reveal: this shot slides past a named foreground
  // element to uncover what lies beyond. The planes/source are populated only when
  // the orchestrator matched the scene to its applied pick.
  parallax: boolean;
  parallaxAnchor: string | null;
  parallaxBackground: string | null;
  parallaxSource: string | null;
  // CPB (2026-10-19): a real-estate move that starts on a crop of the photo and ends on the
  // full photo (orchestrator `reveal`), or a B2C camera clause the guard rewrote so the
  // camera never shows space outside the photo (orchestrator `camera_reveal_guard`).
  reveal: SceneReveal | null;
  cameraRevealGuard: SceneCameraRevealGuard | null;
};

export type SceneReveal = {
  moveId: string | null;
  moveClass: string | null;
  cameraDirection: string | null;
  cropFactor: number | null;
  /** "start": the move starts on a crop and ends on the full photo; "end": a push-in ends on a crop. */
  windowRole: string | null;
};

export type SceneCameraRevealGuard = {
  revealClass: string | null;
  from: string | null;
  to: string | null;
  level: string | null;
};

function readReveal(value: unknown): SceneReveal | null {
  if (!isRecord(value)) return null;
  return {
    moveId: asString(value.move_id),
    moveClass: asString(value.move_class),
    cameraDirection: asString(value.camera_direction),
    cropFactor: asNumber(value.crop_factor),
    windowRole: asString(value.window_role),
  };
}

function readCameraRevealGuard(value: unknown): SceneCameraRevealGuard | null {
  if (!isRecord(value)) return null;
  return {
    revealClass: asString(value.class),
    from: asString(value.from),
    to: asString(value.to),
    level: asString(value.level),
  };
}

/** "Ends on the full photo: lateral, left, from a 1.25x crop" for an anchored move. */
export function revealLabel(reveal: SceneReveal | null): string | null {
  if (!reveal) return null;
  const parts = [
    reveal.moveClass?.replaceAll("_", " "),
    reveal.cameraDirection,
  ].filter((part): part is string => Boolean(part));
  // A push-in (window_role "end") starts on the full photo and lands on a real crop of it.
  const endsOnCrop =
    reveal.windowRole === "end" || reveal.moveClass === "push_in";
  const crop =
    reveal.cropFactor != null
      ? endsOnCrop
        ? `to a ${reveal.cropFactor.toFixed(2)}x crop`
        : `from a ${reveal.cropFactor.toFixed(2)}x crop`
      : null;
  const detail = [...parts, crop].filter(Boolean).join(", ");
  const head = endsOnCrop
    ? "Ends on a real crop of the photo"
    : "Ends on the full photo";
  return detail ? `${head}: ${detail}` : head;
}

/** A parallax camera direction carries the word "parallax" (matches the orchestrator). */
function directionIsParallax(direction: string | null): boolean {
  return Boolean(direction && direction.toLowerCase().includes("parallax"));
}

/**
 * True when a full Kling prompt carries a scout-pinned parallax move: split off the
 * camera direction and look for "parallax" in it (same marker semantics as the
 * orchestrator's is_parallax_scene). Lets other surfaces (the storyboard) reuse the
 * exact detection without re-implementing the split.
 */
export function promptIsParallax(prompt: string | null | undefined): boolean {
  return directionIsParallax(splitCameraPrompt(prompt ?? null).direction);
}

/**
 * Friendly flavor for a pinned parallax move, from the orchestrator's `camera_move`
 * (build_scene_breakdown): orbit / dolly-in for a subject, drift / push-through for
 * architectural depth. `null` for a non-parallax or unknown move. Forward-compatible:
 * older echoes only have "parallax"/"parallax_push_through".
 */
export function parallaxMoveLabel(
  cameraMove: string | null | undefined,
): string | null {
  switch ((cameraMove ?? "").trim()) {
    case "parallax_orbit":
      return "orbit";
    case "parallax_dolly_in":
      return "dolly-in";
    case "parallax_push_through":
      return "push-through";
    case "parallax":
      return "drift";
    default:
      return null;
  }
}

/**
 * Same flavor derived from the prompt text, for surfaces that carry the prompt but not
 * the resolved move name (the storyboard). Reads the camera direction split off after
 * the marker and matches the fragment wording. `null` when it isn't a parallax move.
 */
export function parallaxMoveLabelFromPrompt(
  prompt: string | null | undefined,
): string | null {
  const direction = splitCameraPrompt(prompt ?? null).direction;
  if (!directionIsParallax(direction)) return null;
  const t = (direction ?? "").toLowerCase();
  if (t.includes("orbit")) return "orbit";
  if (
    t.includes("push-in") ||
    t.includes("push in") ||
    t.includes("falls away")
  )
    return "dolly-in";
  if (t.includes("through the")) return "push-through";
  if (t.includes("lateral") || t.includes("drift")) return "drift";
  return null;
}

/** Build the per-scene breakdown from a loaded script envelope. */
export function deriveSceneBreakdown(
  envelope: Record<string, unknown> | null | undefined,
): SceneBreakdownEntry[] {
  if (!isRecord(envelope)) return [];

  // Preferred: the orchestrator's structured tracking (first-class camera move + flags).
  const diagnostics = isRecord(envelope.diagnostics)
    ? envelope.diagnostics
    : undefined;
  const tracked = diagnostics?.scene_breakdown;
  if (Array.isArray(tracked) && tracked.length > 0) {
    return tracked
      .filter(isRecord)
      .map((row, index) => normalizeTrackedEntry(row, index));
  }

  // Fallback: derive from the raw script scenes (older Echoes with no scene_breakdown).
  const script = isRecord(envelope.script) ? envelope.script : envelope;
  const scenes = Array.isArray(script.video_content)
    ? script.video_content
    : [];
  return scenes
    .filter(isRecord)
    .map((scene, index) => deriveEntryFromScene(scene, index));
}

function normalizeTrackedEntry(
  row: Record<string, unknown>,
  index: number,
): SceneBreakdownEntry {
  const type = asString(row.type);
  const prompt = asString(row.base_prompt) ?? asString(row.prompt);
  const cameraDirection = asString(row.camera_direction);
  return {
    sceneId: scalarId(row.scene_id) ?? index + 1,
    index: typeof row.index === "number" ? row.index : index,
    type,
    // The orchestrator's tracked flag only covers ImageToVideoScene; a prompted
    // StaticImageScene is routed to the SECONDARY provider and animates too.
    animated: row.animated === true || isSecondaryAnimated(type, prompt),
    promoted: row.promoted === true,
    cameraMove: asString(row.camera_move),
    cameraDirection,
    basePrompt: prompt,
    file: asString(row.file),
    length: asNumber(row.length),
    transition: asString(row.transition),
    titleText: asString(row.title_text),
    // Prefer the orchestrator's explicit flag; fall back to the prompt marker so
    // Echoes generated before the flag shipped still light up (their pinned
    // direction already says "parallax").
    parallax: row.parallax === true || directionIsParallax(cameraDirection),
    parallaxAnchor: asString(row.parallax_anchor),
    parallaxBackground: asString(row.parallax_background),
    parallaxSource: asString(row.parallax_source),
    reveal: readReveal(row.reveal),
    cameraRevealGuard: readCameraRevealGuard(row.camera_reveal_guard),
  };
}

function deriveEntryFromScene(
  scene: Record<string, unknown>,
  index: number,
): SceneBreakdownEntry {
  const type = asString(scene.type);
  const prompt = isRecord(scene.video_prompt) ? scene.video_prompt : {};
  const rawPrompt =
    asString(prompt.prompt) ?? asString(prompt.generation_prompt);
  const { base, direction } = splitCameraPrompt(rawPrompt);
  return {
    sceneId: scalarId(scene.id) ?? index + 1,
    index,
    type,
    animated:
      type === "ImageToVideoScene" || isSecondaryAnimated(type, rawPrompt),
    promoted: false, // not knowable from the raw script; only the tracking channel has it
    cameraMove: null, // the move name lives only in the structured tracking channel
    cameraDirection: direction,
    basePrompt: base,
    file: asString(prompt.file),
    length: asNumber(prompt.length),
    transition: asString(scene.transition),
    titleText: asString(prompt.text) ?? asString(prompt.title_card_text),
    // Detectable from the raw prompt marker; the named planes are not (they live
    // only in the structured tracking channel).
    parallax: directionIsParallax(direction),
    parallaxAnchor: null,
    parallaxBackground: null,
    parallaxSource: null,
    reveal: null, // only the tracking channel carries it
    cameraRevealGuard: null,
  };
}

/**
 * The composer animates a StaticImageScene when it carries a non-empty motion
 * prompt (routed through the SECONDARY i2v provider role).
 */
function isSecondaryAnimated(
  type: string | null,
  prompt: string | null,
): boolean {
  return type === "StaticImageScene" && prompt != null;
}

/** Split a Kling prompt into its base prompt and the appended camera direction. */
export function splitCameraPrompt(prompt: string | null): {
  base: string | null;
  direction: string | null;
} {
  if (!prompt) return { base: prompt ?? null, direction: null };
  const markerIndex = prompt.indexOf(CAMERA_PROMPT_MARKER);
  if (markerIndex === -1) return { base: prompt, direction: null };
  const base = trimTrailingPeriod(prompt.slice(0, markerIndex)) || null;
  const direction =
    trimTrailingPeriod(
      prompt.slice(markerIndex + CAMERA_PROMPT_MARKER.length),
    ) || null;
  return { base, direction };
}

function trimTrailingPeriod(value: string) {
  return value.replace(/\.\s*$/, "").trim();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value : null;
}

function asNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function scalarId(value: unknown): string | number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) return value;
  return null;
}
